<template>
  <div class="dashboard-tab">
    <div v-if="loading" class="dash-loading">
      <scale-loader color="#3d6b9a"></scale-loader>
    </div>

    <template v-else>
      <!-- Barra superior: subtítulo + toggle "Fuente de datos". -->
      <div class="dash-toolbar">
        <span class="dash-subtitle">{{ $t('prm_dash_subtitle') }}</span>
        <button class="source-btn" :class="{ active: sourceMode }" @click="sourceMode = !sourceMode">
          <b-icon pack="fas" icon="database" size="is-small"></b-icon>
          <span>{{ $t('prm_summary_source_button') }}</span>
        </button>
      </div>

      <div v-if="sourceMode" class="source-banner">
        <b-icon pack="fas" icon="circle-info" size="is-small"></b-icon>
        <span v-html="$t('prm_summary_source_hint')"></span>
      </div>

      <div class="dash-body">
        <!-- Alcance: eco solo-lectura del que se edita en Expediente · Datos de contrato (s02.scope). -->
        <scope-card :scope="(doc.s02 || {}).scope" :mode="sourceMode" :readonly="true"></scope-card>

        <kpi-ficha :sources="sources" :doc="doc" :dash="dash" :rag="rag" :penalty-total="penaltyTotal"
          :prm-computed="prmComputed" :mode="sourceMode" @save="(p) => saveField(p.path, p.value)"></kpi-ficha>

        <progress-card :sources="sources" :groups="sources.groups || []"
          :poc="prmComputed ? prmComputed.workPct : null" :mode="sourceMode"></progress-card>

        <economics-card :template="template" :mode="sourceMode"></economics-card>

        <div class="dash-grid">
          <margin-card :prm="sources.prm" :total-amount="totalAmount" :mode="sourceMode"></margin-card>
          <billing-card :billing="billing" :mode="sourceMode"></billing-card>
        </div>

        <safety-card :safety="dash.safety" :phases="dash.safetyPhases" :mode="sourceMode" :reset-key="loadCount"
          @save-field="(p) => saveField(p.path, p.value)" @save-list="(items) => saveList('safetyPhases', items)"></safety-card>

        <contract-review-card :doc="doc" :penalty-total="penaltyTotal" :mode="sourceMode"></contract-review-card>

        <incidents-card :incidents="sources.incidents || []" :mode="sourceMode"></incidents-card>
      </div>
    </template>
  </div>
</template>

<script>
  import axios from 'axios'
  import ScaleLoader from 'vue-spinner/src/ScaleLoader.vue'
  import ScopeCard from './scope-card.vue'
  import KpiFicha from './kpi-ficha.vue'
  import ProgressCard from './progress-card.vue'
  import EconomicsCard from './economics-card.vue'
  import MarginCard from './margin-card.vue'
  import BillingCard from './billing-card.vue'
  import SafetyCard from './safety-card.vue'
  import ContractReviewCard from './contract-review-card.vue'
  import IncidentsCard from './incidents-card.vue'
  import { computeRag, billingCardData, penaltyTotal as calcPenalty } from '../summary/summary-model'
  import { coWeights, computeAll, computeTotals, coSums } from '../template/template-model'

  export default {
    name: 'PrmDashboardTab',

    components: {
      ScaleLoader, ScopeCard, KpiFicha, ProgressCard, EconomicsCard,
      MarginCard, BillingCard, SafetyCard, ContractReviewCard, IncidentsCard,
    },

    props: {
      project: { type: Object, required: true },
      prmVersion: { type: Number, default: 0 }, // sube al importar el PRM → recargamos datos derivados
    },

    data: () => ({
      loading: true,
      sourceMode: false, // modo "Fuente de datos": marca los campos manuales y muestra el origen del resto
      loadCount: 0,
      doc: { s03: {}, s05: [], meta: {} },   // documento del Resumen (solo lectura aquí)
      dash: { safety: {}, safetyPhases: [] }, // datos manuales propios del Dashboard (incidencias ya vienen de Issues real)
      sources: {},
      template: null,                        // Plantilla PRM (encaje por partida + COs + PoC)
      today: new Date().toISOString().slice(0, 10),
    }),

    computed: {
      base () {
        return `/api/v2/project/${this.project.id}/dashboard-prm/dashboard`
      },
      totalAmount () {
        return (this.sources.prm && this.sources.prm.totalAmount) || 0
      },
      // Valores del PRM que necesitan la plantilla completa: PoC (coste incurrido / coste total)
      // y las Change Orders por estado (mismo cálculo que el PANEL DE IMPACTO del PRM: lado coste).
      prmComputed () {
        const t = this.template
        if (!t || !Array.isArray(t.structure) || !t.structure.length) return null
        const cos = (t.columns && t.columns.cos) || []
        const monthIds = ((t.columns && t.columns.months) || []).map((c) => c.id)
        const coIds = cos.map((c) => c.id)
        const totals = computeTotals(
          computeAll(t.structure, t.values || {}, monthIds, coIds, coWeights(cos, 'confirmed')),
          t.structure, monthIds, coIds
        )
        // COs por estado incluyendo la fila de Ventas (mismo criterio que Control económico).
        const { confirmed, expected } = coSums(totals, cos)
        return {
          workPct: totals.cost.workPct,
          coConfirmed: confirmed,
          coExpected: expected,
        }
      },
      // Incidencias OSH abiertas = suma de las abiertas de las fases de safety (derivado).
      oshOpen () {
        return (this.dash.safetyPhases || []).reduce((s, r) => s + (Number(r.openIncidents) || 0), 0)
      },
      // Estado RAG del proyecto: avance + margen + riesgo (S05) + incidencias OSH (safety).
      rag () {
        const p = this.sources.progress || {}
        const prm = this.sources.prm || {}
        return computeRag({
          progressReal: p.real,
          progressScheduled: p.scheduled,
          marginActual: prm.actualMargin,
          marginCommercial: prm.commercialMargin,
          risks: this.doc.s05,
          oshOpen: this.oshOpen,
        })
      },
      // Penalidad total = min(Σ calculadas, importe total × CAP%). Del Resumen (S03).
      penaltyTotal () {
        return calcPenalty((this.doc.s03 || {}).penaltyMilestones, this.totalAmount, (this.doc.meta || {}).penaltyCapPct)
      },
      // Facturación, cobros e hitos (S03): fuente única, del plan de cobro real (hitos + facturas).
      billing () {
        return billingCardData(this.doc.s03, this.totalAmount, this.today)
      },
    },

    watch: {
      'project.id': { immediate: true, handler () { this.load() } },
      // Al importar el PRM o reabrir el Dashboard: recarga en silencio los datos derivados.
      prmVersion (v) { if (v > 0) this.load(true) },
    },

    methods: {
      async load (silent = false) {
        if (!silent) this.loading = true
        this.loadCount++
        try {
          const [record, template] = await Promise.all([
            axios.get(this.base),
            axios.get(`/api/v2/project/${this.project.id}/dashboard-prm/template`).catch(() => null),
          ])
          this.doc = record.data.data.doc
          this.dash = record.data.data.dashboard
          this.sources = record.data.data.sources
          this.template = template ? template.data.data : null
        } catch (e) {
          if (!silent) this.$notify.error((e && e.error_msg) || this.$t('error_retrieving_data'))
        } finally {
          if (!silent) this.loading = false
        }
      },

      // Autosave de un campo escalar del Dashboard.
      saveField (path, value) {
        this.setByPath(path, value)
        axios.put(`${this.base}/field`, { path, value })
          .catch(() => this.$notify.error(this.$t('error_saving_data')))
      },

      // Guarda una lista completa del Dashboard (fases de safety). Las incidencias son solo lectura desde Issues.
      saveList (path, items) {
        this.$set(this.dash, path, items)
        axios.put(`${this.base}/list`, { path, items })
          .catch(() => this.$notify.error(this.$t('error_saving_data')))
      },

      // Fija un valor en el documento del Dashboard por ruta ('scope', 'safety.oshOpen'...).
      setByPath (path, value) {
        const parts = path.split('.')
        let node = this.dash
        for (let i = 0; i < parts.length - 1; i++) {
          if (node[parts[i]] == null) this.$set(node, parts[i], {})
          node = node[parts[i]]
        }
        this.$set(node, parts[parts.length - 1], value)
      },
    },
  }
</script>

<style lang="scss" scoped>
  .dashboard-tab { min-width: 0; }
  .dash-loading { display: flex; justify-content: center; padding: 60px 0; }

  .dash-toolbar { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
  .dash-subtitle { font-size: 12px; color: #6b7787; }
  .source-btn {
    margin-left: auto; display: inline-flex; align-items: center; gap: 6px;
    border: 1px solid #e0b52a; background: #fff; color: #8a6d00;
    font-size: 11px; font-weight: 600; padding: 5px 10px; border-radius: 6px; cursor: pointer;
  }
  .source-btn.active { background: #fff4d6; }
  .source-btn.active:hover { background: #fbecc0; }

  .source-banner {
    display: flex; align-items: flex-start; gap: 8px; margin-bottom: 10px;
    border: 1px solid #e0b52a; background: #fff4d6; color: #8a6d00;
    border-radius: 6px; padding: 8px 12px; font-size: 11px; line-height: 1.45;
  }
  .source-banner ::v-deep b { font-weight: 700; }

  .dash-body { display: flex; flex-direction: column; gap: 12px; }
  // Dos columnas (margen | facturación); en pantallas estrechas se apilan.
  .dash-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(340px, 1fr)); gap: 12px; }
</style>
